// ABOUTME: Index of the "Linked by" groups of the search result nodes, by node id and linking hierarchy.
// ABOUTME: The data-table drops these fields from its rows, so the cells read them here; columns follow the hierarchies seen.

export type LinkedByColumnRef = {
    nodeId: string;
    code: string;
    listName?: string | null;
    label?: string | null;
};

export type LinkedByColumnGroup = {
    hierarchyId: string;
    hierarchyCode?: string | null;
    totalCount: number;
    items: LinkedByColumnRef[];
};

/** linking hierarchy id → its code (the column header) */
export type LinkedByHierarchies = Record<string, string>;

type SearchEdge =
    | { node?: { id?: string | null; linkedByColumns?: LinkedByColumnGroup[] | null } | null }
    | null
    | undefined;

export const LINKED_BY_COLUMN_PREFIX = 'linkedBy-';

/**
 * The label a "Linked by" cell shows for a linking node: its label content, then its listName, then
 * its code. The column filter matches the same fallback server-side (`linkedByLabel`).
 */
export const linkedByRefLabel = (ref: LinkedByColumnRef) => ref.label || ref.listName || ref.code;

/** Grid column id of the "Linked by" column of a linking hierarchy. */
export const linkedByColumnId = (hierarchyId: string) => `${LINKED_BY_COLUMN_PREFIX}${hierarchyId}`;

/** Linking hierarchy of a "Linked by" column id. */
export const linkedByColumnHierarchyId = (columnId: string) =>
    columnId.slice(LINKED_BY_COLUMN_PREFIX.length);

/** Search filter field the "Linked by" columns filter on, with the linking hierarchy in `linkedHierarchy`. */
export const LINKED_BY_LABEL_FILTER_FIELD = 'system-linkedByLabel';

/**
 * The search filters with those of the "Linked by" columns, sent by the data-table on the column
 * id, turned into the `linkedByLabel` filter of their linking hierarchy: the server matches it on
 * the label the cells show (label content, then listName, then code).
 */
export const toLinkedByLabelFilters = <
    T extends { field: string; linkedHierarchy?: string | null },
>(
    filters: T[] = [],
): T[] =>
    filters.map(filter =>
        filter.field.startsWith(LINKED_BY_COLUMN_PREFIX)
            ? {
                  ...filter,
                  field: LINKED_BY_LABEL_FILTER_FIELD,
                  linkedHierarchy: linkedByColumnHierarchyId(filter.field),
              }
            : filter,
    );

// An empty or absent list keeps every linking hierarchy.
const isWanted = (hierarchyId: string, hierarchyIds?: string[] | null) =>
    !hierarchyIds || hierarchyIds.length === 0 || hierarchyIds.includes(hierarchyId);

/** node id → linking hierarchy id → its group, for the nodes of one search page. */
export const indexLinkedByColumns = (
    edges: SearchEdge[],
    hierarchyIds?: string[] | null,
): Map<string, Map<string, LinkedByColumnGroup>> =>
    new Map(
        edges
            .map(edge => edge?.node)
            .filter((node): node is NonNullable<typeof node> & { id: string } => !!node?.id)
            .map(node => [
                node.id,
                new Map(
                    (node.linkedByColumns ?? [])
                        .filter(group => isWanted(group.hierarchyId, hierarchyIds))
                        .map(group => [group.hierarchyId, group]),
                ),
            ]),
    );

/**
 * The linking hierarchies seen so far, grown with the ones of a new page. Only grows, so the
 * columns do not disappear when the user changes page, and returns `seen` itself when the page
 * brings nothing new, so a state holding it does not re-render.
 */
export const mergeLinkedByHierarchies = (
    seen: LinkedByHierarchies,
    edges: SearchEdge[],
    hierarchyIds?: string[] | null,
): LinkedByHierarchies => {
    const added: LinkedByHierarchies = {};
    edges.forEach(edge => {
        (edge?.node?.linkedByColumns ?? []).forEach(group => {
            if (!isWanted(group.hierarchyId, hierarchyIds)) return;
            if (seen[group.hierarchyId] !== undefined || added[group.hierarchyId] !== undefined) {
                return;
            }
            added[group.hierarchyId] = group.hierarchyCode || group.hierarchyId;
        });
    });
    return Object.keys(added).length > 0 ? { ...seen, ...added } : seen;
};

/** The "Linked by" columns, sorted by their header: the hierarchy code without its suffix. */
export const getLinkedByColumnHeaders = (
    hierarchies: LinkedByHierarchies,
): { hierarchyId: string; header: string }[] =>
    Object.entries(hierarchies)
        .map(([hierarchyId, code]) => ({ hierarchyId, header: code.split(':')[0] }))
        .sort((a, b) => a.header.localeCompare(b.header));

/** The group of a row for a linking hierarchy, from the index (by row id). */
export const resolveLinkedByGroup = (
    row: Record<string, unknown> | undefined,
    hierarchyId: string,
    index: Map<string, Map<string, LinkedByColumnGroup>> | undefined,
): LinkedByColumnGroup | null => {
    const id = typeof row?.id === 'string' ? row.id : undefined;
    return (id ? index?.get(id)?.get(hierarchyId) : undefined) ?? null;
};
